/**
 * .gltf 文件夹导入工具：收集拖放文件（含目录递归），重写 .gltf JSON 中的外部资源 URI 为 blob URL。
 */

interface GltfBuffer {
  uri?: string;
}

interface GltfImage {
  uri?: string;
}

interface GltfJson {
  buffers?: GltfBuffer[];
  images?: GltfImage[];
}

/** 从拖放事件中收集所有文件，支持文件夹拖放（通过 webkitGetAsEntry 递归遍历）。 */
export async function collectDroppedFiles(dataTransfer: DataTransfer): Promise<File[]> {
  const items = dataTransfer.items;
  if (items && items.length > 0) {
    const entries: FileSystemEntry[] = [];
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry?.();
      if (entry) entries.push(entry);
    }
    if (entries.length > 0) {
      return collectEntries(entries);
    }
  }
  // Fallback：files 直接可用（Firefox 或普通多文件拖放）
  return Array.from(dataTransfer.files);
}

function collectEntries(entries: FileSystemEntry[]): Promise<File[]> {
  const files: File[] = [];

  const traverse = (entry: FileSystemEntry): Promise<void> =>
    new Promise((resolve) => {
      if (entry.isFile) {
        (entry as FileSystemFileEntry).file(
          (file) => {
            files.push(file);
            resolve();
          },
          () => resolve(),
        );
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        const readBatch = (): void => {
          reader.readEntries(
            (children) => {
              if (children.length === 0) {
                resolve();
              } else {
                void Promise.all(children.map(traverse)).then(readBatch);
              }
            },
            () => resolve(),
          );
        };
        readBatch();
      } else {
        resolve();
      }
    });

  return Promise.all(entries.map(traverse)).then(() => files);
}

/**
 * 将 .gltf 文件及其关联资源（.bin、纹理）重写为自包含的 blob URL。
 * 返回可直接用于加载的 URL 和显示名称；无 .gltf 文件时返回 null。
 */
export async function rewriteGltfToBlobUrl(
  files: File[],
): Promise<{ url: string; name: string } | null> {
  const gltfFile = files.find((f) => f.name.endsWith('.gltf'));
  if (!gltfFile) return null;

  const text = await gltfFile.text();
  const gltf = JSON.parse(text) as GltfJson;

  // 构建路径 → File 映射，支持 webkitRelativePath 和纯文件名匹配
  const fileMap = new Map<string, File>();
  for (const f of files) {
    if (f === gltfFile) continue;
    const relPath = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
    fileMap.set(relPath, f);
    // 也按纯文件名映射，处理 .gltf 中 URI 只写文件名的情况
    fileMap.set(f.name, f);
  }

  // 重写 buffer URI（外部 .bin 文件）
  if (gltf.buffers) {
    for (const buf of gltf.buffers) {
      if (buf.uri && !buf.uri.startsWith('data:')) {
        const file = fileMap.get(buf.uri) ?? fileMap.get(buf.uri.split('/').pop()!);
        if (file) {
          buf.uri = URL.createObjectURL(file);
        }
      }
    }
  }

  // 重写 image URI（外部纹理文件）
  if (gltf.images) {
    for (const img of gltf.images) {
      if (img.uri && !img.uri.startsWith('data:')) {
        const file = fileMap.get(img.uri) ?? fileMap.get(img.uri.split('/').pop()!);
        if (file) {
          img.uri = URL.createObjectURL(file);
        }
      }
    }
  }

  const blob = new Blob([JSON.stringify(gltf)], { type: 'model/gltf+json' });
  const url = URL.createObjectURL(blob);
  return { url, name: gltfFile.name };
}
